import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X, Edit2, Trash2, Bot } from "lucide-react";
import { POST_STATUSES, PLATFORMS, US_STATES, getPlatformColor, getPlatformLabel } from "./constants";
import { PostDialog, type SocialPost } from "./PostDialog";

interface Post extends SocialPost {
  id: string;
}

interface Campaign { id: string; name: string; }
interface Account { id: string; platform: string; username: string | null; handle: string | null; state_code: string | null; }

interface Props {
  posts: Post[];
  isLoading: boolean;
  filterStatus: string;
  filterPlatform: string;
  filterState: string;
  onFilterStatusChange: (v: string) => void;
  onFilterPlatformChange: (v: string) => void;
  onFilterStateChange: (v: string) => void;
  campaigns: Campaign[];
  accounts: Account[];
}

export function PostQueue({
  posts, isLoading,
  filterStatus, filterPlatform, filterState,
  onFilterStatusChange, onFilterPlatformChange, onFilterStateChange,
  campaigns, accounts,
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, extra }: { id: string; status: string; extra?: Record<string, unknown> }) => {
      const updates: Record<string, unknown> = { status, ...extra };
      if (status === 'approved') {
        updates.approved_at = new Date().toISOString();
      }
      const { error } = await supabase
        .from('social_posts' as any)
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social_posts'] });
      toast({ title: 'Post updated' });
    },
    onError: (e: Error) => toast({ title: 'Update failed', description: e.message, variant: 'destructive' }),
  });

  const saveMutation = useMutation({
    mutationFn: async (post: SocialPost) => {
      if (post.id) {
        const { id, ...updates } = post;
        const { error } = await supabase
          .from('social_posts' as any)
          .update(updates)
          .eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('social_posts' as any)
          .insert([post]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social_posts'] });
      setDialogOpen(false);
      setEditingPost(null);
      toast({ title: 'Post saved' });
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('social_posts' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social_posts'] });
      toast({ title: 'Post deleted' });
    },
    onError: (e: Error) => toast({ title: 'Delete failed', description: e.message, variant: 'destructive' }),
  });

  const pendingPosts = posts.filter(p => p.status === 'pending_approval');

  const handleApproveAll = () => {
    if (!pendingPosts.length) return;
    if (!confirm(`Approve all ${pendingPosts.length} pending posts?`)) return;
    Promise.all(pendingPosts.map(p =>
      updateStatusMutation.mutateAsync({ id: p.id!, status: 'approved' })
    )).catch(() => {});
  };

  const getStatusBadge = (status: string) => {
    const s = POST_STATUSES.find(x => x.id === status);
    return s
      ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
      : <span className="text-xs text-muted-foreground">{status}</span>;
  };

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterStatus} onValueChange={onFilterStatusChange}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {POST_STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterPlatform} onValueChange={onFilterPlatformChange}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All Platforms" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            {PLATFORMS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterState} onValueChange={onFilterStateChange}>
          <SelectTrigger className="w-28"><SelectValue placeholder="All States" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="ml-auto flex gap-2">
          {pendingPosts.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="text-green-700 border-green-300 hover:bg-green-50"
              onClick={handleApproveAll}
              disabled={updateStatusMutation.isPending}
            >
              <Check className="w-3.5 h-3.5 mr-1" />
              Approve All Pending ({pendingPosts.length})
            </Button>
          )}
          <Button size="sm" onClick={() => { setEditingPost(null); setDialogOpen(true); }}>
            + New Post
          </Button>
        </div>
      </div>

      {/* Post cards */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading posts...</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border rounded-lg">
          No posts found. Create your first post!
        </div>
      ) : (
        <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
          {posts.map((post: any) => {
            const platformColor = getPlatformColor(post.platform);
            const displayColor = platformColor === '#000000' ? '#374151' : platformColor;

            return (
              <div
                key={post.id}
                className="flex gap-3 p-3 rounded-lg border bg-card hover:bg-accent/30 transition-colors"
              >
                {/* Platform color stripe */}
                <div
                  className="w-1 rounded-full flex-shrink-0"
                  style={{ backgroundColor: displayColor }}
                />

                {/* Main content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    {/* Platform badge */}
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: displayColor }}
                    >
                      {getPlatformLabel(post.platform)}
                    </span>

                    {/* Geo */}
                    {post.state_code && (
                      <span className="text-xs bg-muted px-2 py-0.5 rounded-full">{post.state_code}</span>
                    )}

                    {/* AI badge */}
                    {post.ai_generated && (
                      <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        <Bot className="w-3 h-3" />
                        AI
                      </span>
                    )}

                    {/* Status */}
                    {getStatusBadge(post.status)}

                    {/* Scheduled time */}
                    {post.scheduled_at && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        {new Date(post.scheduled_at).toLocaleString('en-US', {
                          month: 'short', day: 'numeric',
                          hour: 'numeric', minute: '2-digit'
                        })}
                      </span>
                    )}
                  </div>

                  {/* Content preview */}
                  <p className="text-sm text-foreground leading-snug line-clamp-2">
                    {post.content}
                  </p>

                  {/* Denial reason */}
                  {post.denial_reason && (
                    <p className="text-xs text-red-600 mt-1">
                      Denied: {post.denial_reason}
                    </p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-start gap-1 flex-shrink-0">
                  {post.status === 'pending_approval' && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => updateStatusMutation.mutate({ id: post.id, status: 'approved' })}
                        title="Approve"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          const reason = prompt('Denial reason (optional):') ?? '';
                          updateStatusMutation.mutate({ id: post.id, status: 'denied', extra: { denial_reason: reason || null } });
                        }}
                        title="Deny"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setEditingPost(post); setDialogOpen(true); }}
                    title="Edit"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { if (confirm('Delete this post?')) deleteMutation.mutate(post.id); }}
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PostDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        post={editingPost}
        campaigns={campaigns}
        accounts={accounts}
        onSave={data => saveMutation.mutate(data)}
        isPending={saveMutation.isPending}
      />
    </div>
  );
}
